import { Service } from "typedi";
import { v4 as uuidv4 } from "uuid";
import { ContentScriptOutboundMessage, ControllerOutboundMessage } from "../../shared/ChannelProtocol";

const CHANNEL_NAME = "schools-controller";

/**
 * Instance-to-instance messages that travel over the same-origin
 * BroadcastChannel. Every message carries the origin `instanceId` so
 * responses can be routed back to the right controller.
 */
export type InstanceBroadcastMessage =
    | { kind: "hello"; instanceId: string }
    | { kind: "goodbye"; instanceId: string }
    /** Non-leader forwarding a content-script event to the leader. */
    | { kind: "content-script-event"; instanceId: string; event: ContentScriptOutboundMessage }
    /** Leader asking a specific instance to push an outbound message to its content script. */
    | { kind: "to-content-script"; targetInstanceId: string; message: ControllerOutboundMessage }
    /** Leader broadcasting an outbound message to ALL content scripts. */
    | { kind: "broadcast-to-content-scripts"; message: ControllerOutboundMessage }
    /** Leader asking every instance to report its current tab state. */
    | { kind: "request-snapshot-report"; requestId: string }
    /** Instance responding to the leader's snapshot request. */
    | {
          kind: "snapshot-report";
          requestId: string;
          instanceId: string;
          tabId: string;
          url: string;
          title: string;
          favicon?: string;
          active: boolean;
      };

type Handler = (msg: InstanceBroadcastMessage) => void;

@Service()
export class InstanceCoordinator {
    public readonly instanceId: string = uuidv4();
    private channel: BroadcastChannel | null = null;
    private readonly handlers = new Set<Handler>();

    start(): void {
        if (this.channel) return;
        this.channel = new BroadcastChannel(CHANNEL_NAME);
        this.channel.addEventListener("message", (event: MessageEvent<InstanceBroadcastMessage>) => {
            this.handlers.forEach((h) => {
                try { h(event.data); } catch (err) { console.error("[instance-coordinator] handler error", err); }
            });
        });
        this.broadcast({ kind: "hello", instanceId: this.instanceId });
        window.addEventListener("pagehide", () => {
            this.broadcast({ kind: "goodbye", instanceId: this.instanceId });
        });
    }

    stop(): void {
        this.broadcast({ kind: "goodbye", instanceId: this.instanceId });
        this.channel?.close();
        this.channel = null;
    }

    broadcast(message: InstanceBroadcastMessage): void {
        if (!this.channel) {
            console.warn("[instance-coordinator] channel not started, dropping", message);
            return;
        }
        this.channel.postMessage(message);
    }

    onMessage(handler: Handler): () => void {
        this.handlers.add(handler);
        return () => this.handlers.delete(handler);
    }
}
